import { Editor } from "@tiptap/core";
import { BubbleMenu, BubbleMenuProps } from "@tiptap/react/menus";
import { observer } from "mobx-react-lite";
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  MemCommonEditorAction,
  MemCommonEditorActionKind,
  MemCommonEditorEvent,
  MemCommonEditorInstanceRef,
  MemCommonEditorToolbarKind,
  urlModule,
} from "@mem-labs/common-editor";
import { css, cx } from "@/domains/emotion";
import { MDSIconKind } from "@/mds";
import { ZIndex } from "@/domains/design/constants";
import { actions } from "@/actions";
import styled from "@emotion/styled";
import { isMac } from "@/domains/platform/isMac";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import {
  MDSIconButton,
  MDSTooltipPlacement,
  MDSTooltip,
  MDSIconButtonSize,
  MDSTextField,
  MDSTextFieldVariant,
} from "@/mds";

export const FormattingToolbarContext = createContext<MemCommonEditorStore | undefined>(undefined);

export interface FormattingToolbarProps {
  appendTo?: () => HTMLElement;
  editor: Editor;
  editorRef: MemCommonEditorInstanceRef;
  shouldShow: () => MemCommonEditorToolbarKind;
}

export const FormattingToolbar = observer<FormattingToolbarProps>(
  ({ appendTo, editor, editorRef, shouldShow: commonEditorShouldShow }) => {
    const store = useContext(FormattingToolbarContext);

    const [_count, setCount] = useState(0);
    const [url, setUrl] = useState(undefined as string | undefined);
    const urlRef = useRef(url);
    urlRef.current = url;

    const [selectionUrl, setSelectionUrl] = useState(undefined as string | undefined);
    const selectionUrlRef = useRef(selectionUrl);
    selectionUrlRef.current = selectionUrl;

    useLayoutEffect(() => {
      const updateSelectionUrl = () => {
        if (editor.state.selection.empty && !editor.isActive("link")) {
          setSelectionUrl(undefined);
          setUrl(undefined);
          return;
        }
        const attributes = editor.getAttributes("link");
        const href = attributes?.href ? urlModule.resolveMemUrl(attributes?.href) : "";
        setSelectionUrl(href);
      };

      editor.on("update", updateSelectionUrl);
      editor.on("selectionUpdate", updateSelectionUrl);
      return () => {
        editor.off("update", updateSelectionUrl);
        editor.off("selectionUpdate", updateSelectionUrl);
      };
    }, [editor]);

    const saveLinkButtonId = useId();

    const [showLinkToolbar, setShowLinkToolbar] = useState(false);
    const [showInsertTable, setShowInsertTable] = useState(false);

    const handleUrlInputBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      const input = e.currentTarget;
      const parent = input.parentElement;
      if (!parent) return;

      const focusStealingElement = e.relatedTarget;
      if (focusStealingElement?.id === saveLinkButtonId) {
        // We're saving the link.
        return;
      }

      if (parent.contains(focusStealingElement)) {
        input.focus();
        return;
      }

      setUrl(undefined);
    };

    const reFocus = () => {
      editorRef.current?.editor.commands.focus();
    };

    const action = (event: MemCommonEditorAction) => () => {
      if (!editorRef.current?.editor.options.editable) return;

      editorRef.current.dispatchAction(event);
      setCount(prev => prev + 1);
      reFocus();
    };

    const _event = (event: MemCommonEditorEvent) => () => {
      if (!editorRef.current?.editor.options.editable) return;

      editorRef.current.publishEvent(event);
      setCount(prev => prev + 1);
      reFocus();
    };

    const handleNewHyperlink = (e: React.MouseEvent) => {
      const { selection } = editor.state;
      if (editor?.isActive("link") || !selection || selection.empty) {
        return false;
      }

      setUrl("");
      e.stopPropagation();
      return true;
    };

    const handleCopyHyperlink = () => {
      if (!editorRef.current || !selectionUrlRef.current) return;

      actions.copyUrlToClipboard({ url: selectionUrlRef.current });
      reFocus();
    };

    const handleEditHyperlink = () => {
      if (!editorRef.current || !selectionUrlRef.current) return;

      setUrl(selectionUrlRef.current);
    };

    const handleRemoveHyperlink = () => {
      if (!editorRef.current || !selectionUrlRef.current) return;

      editorRef.current.dispatchAction({
        kind: MemCommonEditorActionKind.SetHyperlink,
        payload: { url: undefined },
      });
      reFocus();
    };

    const handleSaveHyperlink = () => {
      if (!editorRef.current) return;

      editorRef.current.dispatchAction({
        kind: MemCommonEditorActionKind.SetHyperlink,
        payload: { url },
      });

      setUrl(undefined);
      reFocus();
    };

    const handleUrlInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "Enter":
          handleSaveHyperlink();
          break;
        case "Escape":
          setUrl(undefined);
          editor.commands.focus();
          e.stopPropagation();
          e.preventDefault();
          break;
      }
    };

    const shouldShow: BubbleMenuProps["shouldShow"] = useCallback(
      ({ editor }: { editor: Editor }) => {
        if (store?.isTableMenuOpen) {
          return false;
        }
        if (!editor.options.editable) {
          return false;
        }
        /**
         * BubbleMenu expects a boolean return value but common-editor returns the kind of toolbar
         * that should be shown. So here we have side-effects to remember which toolbar should be
         * active as handling it manually would require matching the logic internal to BubbleMenu
         * that watches editor events.
         */
        switch (commonEditorShouldShow()) {
          case MemCommonEditorToolbarKind.Disabled:
          case MemCommonEditorToolbarKind.None:
            return false;
          case MemCommonEditorToolbarKind.Formatting:
            setShowLinkToolbar(false);
            setShowInsertTable(true);
            return true;
          case MemCommonEditorToolbarKind.FormattingInTable:
            setShowLinkToolbar(false);
            setShowInsertTable(false);
            return true;
          case MemCommonEditorToolbarKind.Link:
            setShowLinkToolbar(true);
            setShowInsertTable(false);
            return true;
        }
      },
      [commonEditorShouldShow, store?.isTableMenuOpen]
    );

    const bubbleMenuRef = useRef<HTMLDivElement>(null);
    const visibleRef = useRef(false);

    useLayoutEffect(() => {
      /**
       * Make the toolbar uninteractible while dragging the selection.
       *
       * In general we want it to be interactible so that the user can click on the link,
       * hover works, etc.
       * When updating the selection the toolbar is moved to the new position until it reaches the
       * top. During this time we don't need toolbar interactivity.
       *
       * It also causes problems. Since it's anchored after the editor, hovering over it extends
       * the selection to its position, selecting everything up to the end of the document. If the
       * toolbar is above the text it also inverts the selection.
       *
       * To avoid that we disable interactivity (pointer-events CSS property) between
       * mousedown and mouseup.
       */
      const onMouseDown = () => {
        if (!visibleRef.current) return;
        if (bubbleMenuRef.current) {
          bubbleMenuRef.current.style.pointerEvents = "none";
        }
      };

      const onMouseUp = () => {
        if (bubbleMenuRef.current) {
          bubbleMenuRef.current.style.pointerEvents = "auto";
        }
      };

      // Watching the editor gives us events inside it, avoiding toolbar events.
      editor.view.dom.addEventListener("mousedown", onMouseDown);
      // Watching the document gives us all events so if the mouse is lifted outside the editor
      // we also get the event.
      document.addEventListener("mouseup", onMouseUp);

      return () => {
        if ("dom" in editor.view) {
          editor.view.dom.removeEventListener("mousedown", onMouseDown);
        }
        document.removeEventListener("mouseup", onMouseUp);
      };
    }, [editor]);

    const editUrlToolbar = typeof url === "string";
    const urlFocusedToolbar = !editUrlToolbar && showLinkToolbar;
    const defaultToolbar = !editUrlToolbar && !showLinkToolbar;

    useLayoutEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        // Cmd+k / Ctrl+k: open link editor.
        if (visibleRef.current && e.key === "k" && (isMac() ? e.metaKey : e.ctrlKey)) {
          let interceptEvent = false;

          setUrl(url => {
            if (typeof url === "string") return url;

            interceptEvent = true;
            return "";
          });

          // If the link editor is already open, ignore event and let search modal handle the event.
          if (!interceptEvent) return;

          e.stopPropagation();
          return true;
        }
      };

      // Capture to run before the callback that toogles quick search modal.
      document.addEventListener("keydown", onKeyDown, { capture: true });
      return () => {
        document.removeEventListener("keydown", onKeyDown);
      };
    }, []);

    return (
      <StyledBubbleMenu
        ref={bubbleMenuRef}
        updateDelay={0}
        appendTo={appendTo}
        options={{
          placement: "top-start",
          flip: false,
          onShow: () => {
            visibleRef.current = true;
            if (bubbleMenuRef.current) {
              // Ensure interactivity is restored anytime the menu appears.
              bubbleMenuRef.current.style.pointerEvents = "auto";
            }
          },
          onHide: () => {
            visibleRef.current = false;
          },
        }}
        editor={editor}
        shouldShow={shouldShow}
      >
        {editUrlToolbar && (
          <div className={cx(rowStyles, urlRowStyles)}>
            <Expanding>
              <LinkInput
                variant={MDSTextFieldVariant.Form}
                autoFocus
                value={url}
                onBlur={handleUrlInputBlur}
                onFocus={e => e.target.select()}
                onChange={setUrl}
                onKeyDown={handleUrlInputKeyDown}
                placeholder="Enter link"
              />
            </Expanding>
            <Separator />
            <ToolbarButton
              id={saveLinkButtonId}
              tooltip="Save link"
              iconKind={MDSIconKind.Check}
              onClick={handleSaveHyperlink}
            />
          </div>
        )}

        {urlFocusedToolbar && (
          <div className={cx(rowStyles, urlRowStyles)}>
            <a
              href={selectionUrl}
              target="_blank"
              className={cx(urlActionStyles, flex1Styles)}
              rel="noreferrer"
            >
              {selectionUrl}
            </a>
            <Separator />
            <ToolbarButton
              tooltip="Copy link"
              iconKind={MDSIconKind.Copy}
              onClick={handleCopyHyperlink}
            />
            <ToolbarButton
              tooltip="Edit link"
              iconKind={MDSIconKind.Pen}
              onClick={handleEditHyperlink}
            />

            <ToolbarButton
              tooltip="Remove link"
              iconKind={MDSIconKind.Trash}
              onClick={handleRemoveHyperlink}
            />
          </div>
        )}

        {defaultToolbar && (
          <>
            <ToolbarButton
              tooltip="Large header"
              iconKind={MDSIconKind.H1}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleHeaderOneTextFormat,
                payload: null,
              })}
            />
            <ToolbarButton
              tooltip="Medium header"
              iconKind={MDSIconKind.H2}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleHeaderTwoTextFormat,
                payload: null,
              })}
            />

            <ToolbarButton
              bold
              tooltip="Bold"
              iconKind={MDSIconKind.Bold}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleBoldTextFormat,
                payload: null,
              })}
            />
            <ToolbarButton
              tooltip="Italicize"
              iconKind={MDSIconKind.Italic}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleItalicTextFormat,
                payload: null,
              })}
            />
            <ToolbarButton
              tooltip="Underline"
              iconKind={MDSIconKind.Underline}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleUnderlineTextFormat,
                payload: null,
              })}
            />
            <ToolbarButton
              tooltip="Strike-through"
              iconKind={MDSIconKind.Strikethrough}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleStrikethroughTextFormat,
                payload: null,
              })}
            />

            <ToolbarButton
              tooltip="Remove formatting"
              iconKind={MDSIconKind.RemoveFormat}
              onClick={action({
                kind: MemCommonEditorActionKind.RemoveAllTextFormat,
                payload: null,
              })}
            />

            <Separator />

            <ToolbarButton
              tooltip="Numbered list"
              iconKind={MDSIconKind.NumberedList}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleOrderedListTextFormat,
                payload: null,
              })}
            />

            <ToolbarButton
              tooltip="Bulleted list"
              iconKind={MDSIconKind.List}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleBulletListTextFormat,
                payload: null,
              })}
            />

            <ToolbarButton
              tooltip="To-do list"
              iconKind={MDSIconKind.Tasks}
              onClick={action({
                kind: MemCommonEditorActionKind.ToggleChecklistTextFormat,
                payload: null,
              })}
            />

            <Separator />

            <ToolbarButton
              iconKind={MDSIconKind.Link}
              tooltip="Add link"
              onClick={handleNewHyperlink}
            />
            <Disabled>
              <ToolbarButton
                tooltip="Add image"
                iconKind={MDSIconKind.Image}
                // onClick={event({
                //   kind: MemCommonEditorEventKind.RpcRequest,
                //   payload: {
                //     kind: MemCommonEditorRpcKind.InsertImage,
                //     rpcId: `insert-image-${count}`,
                //     payload: {
                //       requestTimestamp: Date.now(),
                //       queryString: "",
                //     },
                //   },
                // })}
                data-tooltip=""
              />
            </Disabled>
            {showInsertTable && (
              <ToolbarButton
                iconKind={MDSIconKind.Table}
                tooltip="Add table"
                onClick={action({
                  kind: MemCommonEditorActionKind.InsertTable,
                  payload: { columns: 3, rows: 3 },
                })}
              />
            )}
            <Disabled>
              <ToolbarButton
                iconKind={MDSIconKind.Paperclip}
                // onClick={event({
                //   kind: MemCommonEditorEventKind.RpcRequest,
                //   payload: {
                //     kind: MemCommonEditorRpcKind.InsertImage,
                //     rpcId: `insert-file-${count}`,
                //     payload: {
                //       requestTimestamp: Date.now(),
                //       queryString: "",
                //     },
                //   },
                // })}
                tooltip="Add file"
              />

              <Separator />

              <ToolbarButton
                tooltip="Smart Edit"
                iconKind={MDSIconKind.Sparkle}
                className={smartEditStyles}
              />
            </Disabled>
          </>
        )}
      </StyledBubbleMenu>
    );
  }
);

const flex1Styles = css({
  flex: 1,
});

const urlActionStyles = css({
  color: `#3cf`,
});

const SeparatorContainer = styled.div`
  display: flex;
  padding: 0 ${({ theme }) => theme.spacing["3"]};
  height: ${({ theme }) => theme.spacing[5]};
  align-items: center;
`;

const SeparatorLine = styled.div`
  background: ${({ theme }) => theme.colors.border.default};
  width: 1px;
  height: ${({ theme }) => theme.spacing[5]};
`;

const Separator = () => {
  return (
    <SeparatorContainer>
      <SeparatorLine />
    </SeparatorContainer>
  );
};

const StyledBubbleMenu = styled(BubbleMenu)`
  position: relative;
  cursor: text;
  display: inline-flex;
  max-width: 700px;
  min-width: 540px;
  width: fit-content;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  background: ${({ theme }) => theme.colors.surface.menu};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  z-index: ${ZIndex.FloatingBar};
`;

const urlRowStyles = css({
  gap: "4px",
  padding: "0px 2px",
  userSelect: "none",
  flex: 1,

  a: {
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    overflow: "hidden",
    padding: "8px 6px",
    textOverflow: "ellipsis",
    flex: 1,
    whiteSpace: "nowrap",
    color: "var(--url-color)",
  },

  input: {
    background: "transparent",
    color: `var(--color-grey-600)`,
    flex: 1,
    fontSize: "13px",
    lineHeight: "26px",
    outline: "none",
    padding: "8px 6px",
    height: "26px",
  },

  span: {
    color: `var(--color-grey-600)`,
    fontSize: "14px",
    fontWeight: "400",
  },
});

const rowStyles = css({
  alignItems: "center",
  display: "flex",
  maxWidth: "calc(100% - 8px)",
  gap: "10px",
});

const StyledIconButton = styled(MDSIconButton)<{ $bold?: boolean }>`
  transition: "ease-in-out 0.1s";

  & .mds-icon {
    font-size: ${({ theme }) => theme.fontSizes["header-s"]};
  }

  &:hover {
    transition: 0.1s ease-in all;
  }
`;

const smartEditStyles = css({
  padding: "6px",

  ".mds-btn-icon, .mds-btn-label": {
    color: "#F22E61",
  },
});

const Disabled: FC<PropsWithChildren> = () => null;

const ToolbarButton = ({
  id,
  iconKind,
  onClick,
  tooltip,
  className,
  bold,
}: {
  id?: string;
  iconKind: MDSIconKind;
  onClick?: (e: React.MouseEvent) => void;
  tooltip: string;
  className?: string;
  bold?: boolean;
}) => {
  return (
    <MDSTooltip
      config={{
        label: tooltip,
        placement: MDSTooltipPlacement.Top,
      }}
    >
      <StyledIconButton
        id={id}
        size={MDSIconButtonSize.Medium}
        className={className}
        iconKind={iconKind}
        onClick={onClick}
        $bold={bold}
      />
    </MDSTooltip>
  );
};

export const Expanding = styled.div`
  flex: 1;
`;

const LinkInput = styled(MDSTextField)`
  border: unset;
  background-color: transparent;
  padding-left: 0;
`;
